import { useCallback, useEffect, useState } from "react";
import { FileStack } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { ReportLink, useReportFromActivities } from "@/lib/reportActivities";
import { StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ACT_STATUS } from "@/components/activities/ActivityDialog";

export const ReportActivities = ({ itv, canGenerate, onDone, reloadKey }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [run, busy] = useReportFromActivities();
  const load = useCallback(() => api.get(`/interventions/${itv.id}/report-activities`).then((r) => setData(r.data)).catch(() => setData({ items: [] })), [itv.id]);
  useEffect(() => { load(); }, [load, reloadKey]);
  if (!data || !data.items.length) return null;
  const pending = data.items.filter((a) => a.status === "completata" && !a.report);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const go = async () => { if (await run(itv.id, sel)) { setSel([]); load(); onDone(); } };
  const label = data.active_report ? `Aggiorna rapportino ${data.active_report.number}` : "Genera rapportino";
  return (
    <div className="mt-4 border-t border-line pt-4" data-testid="itv-report-activities">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Attività e rapportino · {pending.length} da riportare</p>
        {canGenerate && pending.length > 0 && (
          <Button size="sm" className="gap-1" disabled={busy || !sel.length} onClick={go} data-testid="itv-report-from-activities"><FileStack className="h-4 w-4" />{label}{sel.length ? ` (${sel.length})` : ""}</Button>
        )}
      </div>
      <ul className="divide-y divide-line/60">
        {data.items.map((a) => {
          const selectable = canGenerate && a.status === "completata" && !a.report;
          return (
            <li key={a.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`itv-report-activity-${a.id}`}>
              {selectable ? <Checkbox checked={sel.includes(a.id)} onCheckedChange={() => toggle(a.id)} data-testid={`itv-report-activity-check-${a.id}`} /> : <span className="w-4" />}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{a.title}</p>
                <p className="text-xs text-slate-500">{a.completed_at ? `Completata ${fmtDateTime(a.completed_at)}` : a.start_at || a.due_at ? fmtDateTime(a.start_at || a.due_at) : "—"}</p>
              </div>
              <StatusBadge tone={ACT_STATUS[a.status]?.[1]} dot={false}>{ACT_STATUS[a.status]?.[0] || a.status}</StatusBadge>
              {a.report ? <ReportLink report={a.report} testId={`itv-report-activity-in-${a.id}`} />
                : a.status === "completata" && <span className="text-xs font-semibold text-amber-700" data-testid={`itv-report-activity-pending-${a.id}`}>Da riportare</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
